import { BrandCartUiTutorialSlide } from './brandCartUiTutorial';
import { z } from 'zod';

export const PaletteColor = z
    .object({
        main: z.string(),
        light: z.string(),
        dark: z.string(),
        contrastText: z.string(),
    })
    .partial();

export const SimplePaletteColor = z.object({
    main: z.string(),
    contrastText: z.string(),
});

export const Palette = z.object({
    primary: PaletteColor,
    secondary: PaletteColor,
    discount: SimplePaletteColor,
    free: SimplePaletteColor,
    cartBackground: PaletteColor,
    navigationBackground: PaletteColor,
    disruptor: SimplePaletteColor.default({ main: '#fdd764', contrastText: '#ffffff' }),
});

export const BrandImages = z.object({
    // Logos
    logoBlack: z.string().nullable(),
    logoWhite: z.string().nullable(),
    logoColor: z.string().nullable(),
    logoCompany: z.string().nullable(),

    // Background
    cartBackground: z.string().nullable(),

    // PLU
    enterPlu: z.string().nullable(),
});

export const BrandIcons = z.object({
    search: z.string().nullable(),
    pluHierarchy: z.string().nullable(),
    pluSearch: z.string().nullable(),
    promotion: z.string().nullable(),
    sosActive: z.string().nullable(),
    sosInactive: z.string().nullable(),
    lockActive: z.string().nullable(),
    lockInactive: z.string().nullable(),
    cart: z.string().nullable(),
    cartPlus: z.string().nullable(),
    navigation: z.string().nullable(),
    arrowLeftTutorial: z.string().nullable(),
    arrowRightTutorial: z.string().nullable(),
    arrowBack: z.string().nullable(),
    user: z.string().nullable(),
    location: z.string().nullable(),
    edit: z.string().nullable(),
    exitPopUp: z.string().nullable(),
    exit: z.string().nullable(),
    startShopping: z.string().nullable(),
    startPayment: z.string().nullable(),
    checkout: z.string().nullable(),
    payByApp: z.string().nullable(),
    payAtRegister: z.string().nullable(),
    navigate: z.string().nullable(),
    recipes: z.string().nullable(),
    language: z.string().nullable(),
    close: z.string().nullable(),
    tutorial: z.string().nullable(),
    dataPrivacy: z.string().nullable(),
    termsAndConditions: z.string().nullable(),
    coupon: z.string().nullable(),
    flagOrganic: z.string().nullable(),
    flagLactoseFree: z.string().nullable(),
    flagGlutenFree: z.string().nullable(),
    flagRegional: z.string().nullable(),
    flagVegetarian: z.string().nullable(),
    flagVegan: z.string().nullable(),
});

export const CustomerIdType = z.enum(['card', 'mobileApp']);
export type CustomerIdType = z.infer<typeof CustomerIdType>;

export const CustomerId = z.object({
    id: z.string(),
    name: z.string().nullable(),
    image: z.string().nullish(),
    logoImage: z.string().nullish(),
    useImageAsLogoImage: z.boolean().default(true),
    color: SimplePaletteColor,
    pointsText: z.string().nullable().default(null),
    showOnWelcomeScreen: z.boolean().default(true),
    showAppStores: z.boolean().default(true),
    type: CustomerIdType.default('card'),
    styleId: z.string().nullish(),
});

export const Fonts = z.enum(['Ubuntu', 'MedievalSharp', 'Roboto', 'Rye', 'Payback']);

export const EmailBrandingImages = z.object({
    logoEmailLightMode: z.string().nullish(),
    logoEmailDarkMode: z.string().nullish(),
});

export const EmailBranding = z.object({
    images: EmailBrandingImages.nullish(),
});

export const Brand = z.object({
    _id: z.string(),
    cartBrand: z.string().optional(),
    cartBrandShort: z.string().optional(),
    storeBrand: z.string().optional(),
    loyaltyCard: z.string().optional(),
    palette: Palette.partial().default({}),
    images: BrandImages.partial().default({}),
    icons: BrandIcons.partial().default({}),
    emails: EmailBranding.nullish(),
    customIcons: z.record(z.string(), z.string().nullable()).default({}),
    customerIds: z.array(CustomerId).default([]).nullish(),
    borderRadius: z.union([z.number(), z.string()]).optional(),
    headerDisplayTotal: z.boolean().optional().default(false),
    headerUsePrimaryColor: z.boolean().optional().default(false),
    priceLayoutAlt: z.boolean().optional().default(false),
    promotionsLayoutAlt: z.boolean().optional().default(false),
    highlightAddToBasket: z.boolean().optional().default(false),
    fontFamily: z.string().nullish(),
    cartUiTutorialSlides: z.array(BrandCartUiTutorialSlide).default([]).nullish(),
});

export const alternativeBrandingActivationButton = z.object({
    color: z.string().nullish(),
    contrastColor: z.string().nullish(),
    activatedVariant: z.enum(['contained', 'outlined', 'text']).nullish(),
    alwaysActivated: z.boolean()?.nullish(),
});

export type AlternativeBrandingActivationButton = z.infer<typeof alternativeBrandingActivationButton>;
export type Fonts = z.infer<typeof Fonts>;
export type PaletteColor = z.infer<typeof PaletteColor>;
export type SimplePaletteColor = z.infer<typeof SimplePaletteColor>;
export type Palette = z.infer<typeof Palette>;
export type BrandImages = z.infer<typeof BrandImages>;
export type BrandIcons = z.infer<typeof BrandIcons>;
export type CustomerId = z.infer<typeof CustomerId>;
export type Brand = z.infer<typeof Brand>;
export type EmailBranding = z.infer<typeof EmailBranding>;
export type EmailBrandingImages = z.infer<typeof EmailBrandingImages>;
